import { combine, createEvent, createStore, sample } from 'effector'
import { $prescriptionCatalogFlat, $selectedPrescription, prescriptionGoodsFx } from '@entities/prescription/model'
import { $shipment } from '@entities/shipment/model'
import { CanUseErecipe, ICanUseErecipe } from '@entities/prescription/contract'
import { createJsonQuery, declareParams } from '@farfetched/core'
import { IShipment } from 'src/shared/contract/shipment'
import { getPrescriptionCanUseErecipeUrl } from '@entities/prescription/api'
import { runtypeContract } from '@farfetched/runtypes'
import { $basket } from '@entities/basket/model'
import { IGoodBasket } from '@entities/basket/contract'
import { $currentPath } from '@entities/route/model'

export const onBasketPageMount = createEvent()
export const onBasketPageUnmount = createEvent()

export const $isDelivery = createStore(false)

export const toggleAvailableErecipeManual = createEvent()
export const $isAvailableErecipeManual = createStore(false).on(toggleAvailableErecipeManual, (state) => !state)

export const setIsDelivery = createEvent<boolean>()

sample({
    clock: setIsDelivery,
    target: $isDelivery
})
export const toggleIsDelivery = createEvent<boolean>()

export const prescriptionCanUseStatus = createJsonQuery({
    params: declareParams<IShipment>(),
    request: {
        method: 'POST',
        body: (shipment) => shipment,
        headers: {
            //Authorization: getTokenFromCookie(),
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        url: () => getPrescriptionCanUseErecipeUrl()
    },
    serialize: 'ignore',
    initialData: {
        delivery: {
            isErecipeInput: false
        },
        pickup: {
            isErecipeInput: false
        }
    } as ICanUseErecipe,
    response: {
        contract: runtypeContract(CanUseErecipe)
    }
})

sample({
    source: onBasketPageMount,
    clock: $shipment,
    fn: (_, shipment) => shipment,
    target: prescriptionCanUseStatus.start
})
sample({
    clock: $currentPath,
    source: $shipment,
    filter: (_, path) => !!path && path.includes('/order/basket'),
    target: prescriptionCanUseStatus.start
})
export const requestPrescriptionGoods = createEvent()

function isPrescription(prescriptionCanUse: ICanUseErecipe, isDelivery: boolean) {
    const isAvailableDelivery = isDelivery && prescriptionCanUse.delivery.isErecipeInput
    const isAvailablePickup = !isDelivery && prescriptionCanUse.pickup.isErecipeInput
    return isAvailablePickup || isAvailableDelivery
}

sample({
    clock: requestPrescriptionGoods,
    source: {
        isDelivery: $isDelivery,
        prescriptionCanUse: prescriptionCanUseStatus.$data,
        prescription: $selectedPrescription,
        shipment: $shipment
    },
    filter: ({ prescription }) => prescription && prescription.length > 0,
    fn: ({ prescription, shipment }) => ({
        prescriptionIds: prescription.map((x) => x.prescriptionGuid),
        shipment
    }),
    target: prescriptionGoodsFx
})

/*sample({
    clock: onBasketPageMount,
    target: requestPrescriptionGoods
})*/
sample({
    clock: $isDelivery,
    target: requestPrescriptionGoods
})
sample({
    clock: prescriptionCanUseStatus.$data,
    target: requestPrescriptionGoods
})
sample({
    clock: $shipment,
    source: $currentPath,
    filter: (path) => path.includes('prescription'),
    target: requestPrescriptionGoods
})
export const $isBasketPrescription = combine(
    {
        prescriptionCanUse: prescriptionCanUseStatus.$data,
        isDelivery: $isDelivery
    },
    ({ prescriptionCanUse, isDelivery }) => {
        return isPrescription(prescriptionCanUse, isDelivery)
    }
)

export const $basketPrescriptionPositions = combine(
    {
        basket: $basket,
        prescriptionCanUse: prescriptionCanUseStatus.$data,
        prescriptionCatalog: $prescriptionCatalogFlat,
        isDelivery: $isDelivery
    },
    ({ basket, prescriptionCanUse, isDelivery, prescriptionCatalog }): IGoodBasket[] => {
        if (!isPrescription(prescriptionCanUse, isDelivery)) return []
        return basket.filter(
            (x) =>
                ((isDelivery && x.isAvailableDeliveryByErecipe) || (!isDelivery && x.isAvailablePickupByErecipe)) &&
                !prescriptionCatalog.some((pc) => pc.goodsInfo.webData.goodId === x.webData.goodId) &&
                ((isDelivery && !x.deliveryStatus?.isAvailableForDelivery) || !isDelivery)
        )
    }
)

export const $basketAvailablePosition = combine(
    {
        basket: $basket,
        prescriptionCanUse: prescriptionCanUseStatus.$data,
        prescriptionCatalog: $prescriptionCatalogFlat,
        isDelivery: $isDelivery
    },
    ({ basket, prescriptionCanUse, isDelivery, prescriptionCatalog }): IGoodBasket[] => {
        if (!isPrescription(prescriptionCanUse, isDelivery) || !isDelivery) return basket
        return basket.filter(
            (x) =>
                !x.isAvailableDeliveryByErecipe ||
                prescriptionCatalog.some((pc) => pc.goodsInfo.webData.goodId === x.webData.goodId)
        )
    }
)

export const $isErecipePositions = combine(
    {
        basket: $basket,
        prescriptionCanUse: prescriptionCanUseStatus.$data,
        prescriptionCatalog: $prescriptionCatalogFlat,
        isDelivery: $isDelivery
    },
    ({ basket, prescriptionCanUse, isDelivery, prescriptionCatalog }): boolean => {
        if (!isPrescription(prescriptionCanUse, isDelivery)) return false
        return basket.some(
            (x) =>
                ((isDelivery && x.isAvailableDeliveryByErecipe) || (!isDelivery && x.isAvailablePickupByErecipe)) &&
                prescriptionCatalog.some((pc) => pc.goodsInfo.webData.goodId === x.webData.goodId)
        )
    }
)
